/**
 * LoginActivityTable — Login attempt history with filtering (B.7)
 */

import { useState } from 'react';
import { useLoginAttempts } from '../../hooks/useSecurity';
import { Loader2, CheckCircle2, XCircle, Lock, Shield, Globe, Monitor } from 'lucide-react';

const RESULT_BADGE: Record<string, { label: string; color: string; icon: React.ElementType }> = {
  success: { label: 'Success', color: 'bg-ems-green/10 text-ems-green', icon: CheckCircle2 },
  failure: { label: 'Failed', color: 'bg-ems-red/10 text-ems-red', icon: XCircle },
  locked: { label: 'Locked', color: 'bg-ems-amber/10 text-ems-amber', icon: Lock },
};

const ORIGIN_BADGE: Record<string, { label: string; color: string; icon: React.ElementType }> = {
  vpn: { label: 'VPN', color: 'bg-ems-sky/10 text-ems-sky', icon: Shield },
  cloud: { label: 'Cloud', color: 'bg-ems-bg-card-hover text-ems-text-muted', icon: Globe },
  local: { label: 'Local', color: 'bg-ems-green/10 text-ems-green', icon: Monitor },
};

function formatTime(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

export function LoginActivityTable() {
  const [emailFilter, setEmailFilter] = useState('');
  const [resultFilter, setResultFilter] = useState<string | undefined>();
  const [page, setPage] = useState(0);
  const limit = 25;

  const { data, isLoading } = useLoginAttempts({
    email: emailFilter || undefined,
    result: resultFilter,
    limit,
    offset: page * limit,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-ems-text-muted" />
      </div>
    );
  }

  const attempts = data?.attempts || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / limit);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-ems-text-primary">
          Login Activity ({total})
        </h3>
        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder="Filter by email..."
            value={emailFilter}
            onChange={(e) => { setEmailFilter(e.target.value); setPage(0); }}
            className="text-xs border border-ems-border rounded-lg px-2 py-1 w-40 bg-ems-bg text-ems-text-primary focus:outline-none focus:ring-2 focus:ring-ems-green/20"
          />
          <select
            value={resultFilter || ''}
            onChange={(e) => { setResultFilter(e.target.value || undefined); setPage(0); }}
            className="text-xs border border-ems-border rounded-lg px-2 py-1 bg-ems-bg text-ems-text-primary focus:outline-none focus:ring-2 focus:ring-ems-green/20"
          >
            <option value="">All results</option>
            <option value="success">Success</option>
            <option value="failure">Failed</option>
            <option value="locked">Locked</option>
          </select>
        </div>
      </div>

      {attempts.length === 0 ? (
        <p className="text-sm text-ems-text-muted py-4 text-center">No login attempts found.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-ems-text-muted border-b border-ems-border">
                <th className="pb-2 pr-3 font-medium">Time</th>
                <th className="pb-2 pr-3 font-medium">Email</th>
                <th className="pb-2 pr-3 font-medium">Result</th>
                <th className="pb-2 pr-3 font-medium">Reason</th>
                <th className="pb-2 pr-3 font-medium">IP</th>
                <th className="pb-2 pr-3 font-medium">Origin</th>
                <th className="pb-2 font-medium">#</th>
              </tr>
            </thead>
            <tbody>
              {attempts.map((a) => {
                const result = RESULT_BADGE[a.result] || RESULT_BADGE.failure;
                const origin = ORIGIN_BADGE[a.login_origin] || ORIGIN_BADGE.cloud;

                return (
                  <tr key={a.attempt_id} className="border-b border-ems-border">
                    <td className="py-2 pr-3 text-ems-text-muted whitespace-nowrap">{formatTime(a.created_at)}</td>
                    <td className="py-2 pr-3 font-medium text-ems-text-primary">{a.email}</td>
                    <td className="py-2 pr-3">
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${result.color}`}>
                        <result.icon className="w-3 h-3" />
                        {result.label}
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-ems-text-muted">
                      {a.failure_reason ? a.failure_reason.replace(/_/g, ' ') : '—'}
                    </td>
                    <td className="py-2 pr-3 font-mono text-ems-text-muted">{a.source_ip}</td>
                    <td className="py-2 pr-3">
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${origin.color}`}>
                        {origin.label}
                      </span>
                    </td>
                    <td className="py-2 text-ems-text-muted">{a.attempt_count}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-ems-border">
          <span className="text-xs text-ems-text-muted">
            Page {page + 1} of {totalPages}
          </span>
          <div className="flex gap-1">
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-2 py-1 text-xs rounded border border-ems-border disabled:opacity-50 hover:bg-ems-bg-card-hover text-ems-text-secondary"
            >
              Prev
            </button>
            <button
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="px-2 py-1 text-xs rounded border border-ems-border disabled:opacity-50 hover:bg-ems-bg-card-hover text-ems-text-secondary"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
